import React from 'react';
import RecipeBookRow from './RecipeBookRow';
import { SHOW_UNIT_COLUMN, formatQuantity, rowKeyOf } from './recipeBookStore';

/**
 * Appendix A — COMPLIMENTARY DETAILS
 * Appendix B — SAVED PREPARATIONS
 * Appendix C — SAVED PREPARATION USAGE SUMMARY  (read-only, derived: spec §3, §8 row 8)
 *
 * The two editable headers are §8 rows 4 and 6:
 *   • "N Per Plate"  — an integer ≥ 1
 *   • "Batch - N"    — the BATCH NUMBER ONLY; its unit is locked and, per the product
 *                      owner's quantity-only requirement, not rendered at all
 * Neither header has a delete control (§8.3).
 */

function HeaderNumber({ label, row, store, ariaLabel }) {
  const key = rowKeyOf(row);
  const pending = store.pendingChanges.get(key);
  const conflict = store.conflicts.get(key);
  const value = pending ? pending.raw : formatQuantity(row.quantity ?? row.per_plate ?? row.batch_quantity);

  const cls = ['rb-headnum'];
  if (conflict) cls.push('rb-headnum-conflict');
  else if (pending && !pending.valid) cls.push('rb-headnum-invalid');
  else if (pending) cls.push('rb-headnum-edited');

  return (
    <span className={cls.join(' ')}>
      <input
        type="text"
        inputMode="decimal"
        className="rb-qty-input rb-qty-input-head"
        value={value}
        aria-label={ariaLabel}
        onChange={(e) => store.setValue(row, e.target.value)}
      />
      <span className="rb-headnum-label">{label}</span>
      {pending && !pending.valid && <em className="rb-error">{pending.error}</em>}
      {pending && pending.valid && <em className="rb-was">was {formatQuantity(pending.oldValue)}</em>}
      {conflict && (
        <em className="rb-conflict">
          now {formatQuantity(conflict.theirValue)}
          <button type="button" className="rb-mini" onClick={() => store.keepMine(key, conflict.theirVersion)}>Keep mine</button>
          <button type="button" className="rb-mini" onClick={() => store.takeTheirs(key)}>Take theirs</button>
        </em>
      )}
    </span>
  );
}

function IngredientTable({ rows, store, emptyText, warningFor }) {
  const colSpan = SHOW_UNIT_COLUMN ? 5 : 4;
  return (
    <table className="rb-table">
      <thead>
        <tr>
          <th className="rb-sno">S.No</th>
          <th className="rb-name">INGREDIENT</th>
          <th className="rb-qty">QTY</th>
          {SHOW_UNIT_COLUMN && <th className="rb-unit">UNIT</th>}
          <th className="rb-actions" aria-label="Remove" />
        </tr>
      </thead>
      <tbody>
        {rows.length === 0 ? (
          <tr className="rb-empty-row"><td colSpan={colSpan}>{emptyText}</td></tr>
        ) : (
          rows.map((row, i) => {
            const key = rowKeyOf(row);
            return (
              <RecipeBookRow
                key={key}
                row={row}
                serial={i + 1}
                pendingChange={store.pendingChanges.get(key)}
                pendingDelete={store.pendingDeletes.get(key)}
                conflict={store.conflicts.get(key)}
                onChange={store.setValue}
                onToggleDelete={store.toggleDelete}
                onKeepMine={store.keepMine}
                onTakeTheirs={store.takeTheirs}
                deleteWarning={warningFor ? warningFor(row, rows) : null}
              />
            );
          })
        )}
      </tbody>
    </table>
  );
}

export function AppendixComplimentary({ entries, store }) {
  if (!entries || entries.length === 0) return null;
  return (
    <section className="rb-appendix" id="rb-appendix-a">
      <h2 className="rb-appendix-title">Appendix A · Complimentary Details</h2>
      {entries.map((comp) => {
        const headerRow = {
          scope: 'comp_rate',
          comp_id: comp.comp_id,
          menu_item_id: comp.menu_item_id,
          name: comp.name,
          per_plate: comp.per_plate,
          unit_symbol: 'servings',
          row_version: comp.per_plate_row_version,
          editable: true,
          deletable: false,
        };
        return (
          <article className="rb-appendix-block" key={`${comp.comp_id}:${comp.menu_item_id}`}>
            <header className="rb-appendix-head">
              <h3>{comp.name}</h3>
              <HeaderNumber
                label="Per Plate"
                row={headerRow}
                store={store}
                ariaLabel={`Per-plate rate for ${comp.name}`}
              />
              <span className="rb-usedby">
                used by {comp.used_by_item_count} item{comp.used_by_item_count === 1 ? '' : 's'}
              </span>
            </header>
            {comp.unmapped ? (
              // §16 case 8 — an unmapped complimentary item is read-only and says where
              // to go to fix it, rather than offering an edit that would write nothing.
              <p className="rb-empty">
                No Ingredients Mapped —{' '}
                <span className="rb-hint">map it in Customization Mapping</span>
              </p>
            ) : (
              <IngredientTable
                rows={comp.ingredients}
                store={store}
                emptyText="No Ingredients Mapped"
                warningFor={(row, rows) =>
                  rows.length === 1
                    ? `${comp.name} will have no ingredients and will show as unmapped`
                    : `affects every dish using ${comp.name}`
                }
              />
            )}
          </article>
        );
      })}
    </section>
  );
}

export function AppendixPreparations({ entries, store }) {
  if (!entries || entries.length === 0) return null;
  return (
    <section className="rb-appendix" id="rb-appendix-b">
      <h2 className="rb-appendix-title">Appendix B · Saved Preparations</h2>
      {entries.map((prep) => {
        const headerRow = {
          scope: 'prep_batch',
          prep_id: prep.prep_id,
          name: prep.name,
          batch_quantity: prep.batch_quantity,
          unit_symbol: prep.batch_unit_symbol,
          row_version: prep.batch_row_version,
          editable: true,
          deletable: false,
        };
        return (
          <article className="rb-appendix-block" key={prep.prep_id}>
            <header className="rb-appendix-head">
              <h3>{prep.name}</h3>
              <HeaderNumber
                label="Batch"
                row={headerRow}
                store={store}
                ariaLabel={`Batch quantity for ${prep.name}`}
              />
              <span className="rb-usedby">
                used by {prep.used_by_item_count} item{prep.used_by_item_count === 1 ? '' : 's'}
              </span>
            </header>
            <IngredientTable
              rows={prep.ingredients}
              store={store}
              emptyText="No Ingredients Mapped"
              warningFor={() =>
                prep.used_by_item_count > 0
                  ? `moves the cost of ${prep.used_by_item_count} dish${prep.used_by_item_count === 1 ? '' : 'es'}`
                  : null
              }
            />
          </article>
        );
      })}
    </section>
  );
}

/**
 * Custom Plates — the book's closing section. Every dish saved in Item Mapping under
 * "Custom plates", each with its own saved plate count and its quantities for that many
 * plates. Read-only and derived: the editable per-plate rows are in the dish's own
 * section above; edit there (or in Item Mapping) and this recomputes on reload.
 */
export function CustomPlatesSection({ entries }) {
  if (!entries || entries.length === 0) return null;
  return (
    <section className="rb-appendix" id="rb-custom-plates" data-testid="rb-custom-plates">
      <h2 className="rb-appendix-title">Custom Plates</h2>
      <p className="rb-readonly-note">
        Items saved in Item Mapping under Custom plates, with quantities for their saved number of plates.
      </p>
      {entries.map((entry, index) => (
        <article className="rb-appendix-block" key={entry.menu_item_id}>
          <header className="rb-appendix-head">
            <h3>{index + 1}. {entry.name}</h3>
            <span className="rb-headnum rb-headnum-static">
              <strong>{formatQuantity(entry.plates)}</strong>
              <span className="rb-headnum-label">{Number(entry.plates) === 1 ? 'Plate' : 'Plates'}</span>
            </span>
            <span className="rb-usedby">{entry.category}</span>
          </header>
          {entry.rows.length === 0 ? (
            <p className="rb-empty">No raw ingredients saved</p>
          ) : (
            <table className="rb-table rb-table-static">
              <thead>
                <tr>
                  <th className="rb-sno">S.No</th>
                  <th className="rb-name">INGREDIENT</th>
                  <th className="rb-qty">QTY ({formatQuantity(entry.plates)} {Number(entry.plates) === 1 ? 'PLATE' : 'PLATES'})</th>
                  <th className="rb-unit">UNIT</th>
                </tr>
              </thead>
              <tbody>
                {entry.rows.map((row, i) => (
                  <tr key={`${entry.menu_item_id}:${row.kind}:${i}`}>
                    <td className="rb-sno">{i + 1}</td>
                    <td className="rb-name">{row.name}</td>
                    <td className="rb-qty">{formatQuantity(row.quantity)}</td>
                    <td className="rb-unit">{row.unit_symbol}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </article>
      ))}
    </section>
  );
}

/** Appendix C is derived data. Read-only, recomputed on every load (§3, §8 row 8). */
export function AppendixUsageSummary({ entries }) {
  if (!entries || entries.length === 0) return null;
  return (
    <section className="rb-appendix" id="rb-appendix-c">
      <h2 className="rb-appendix-title">Appendix C · Saved Preparation Usage Summary</h2>
      <p className="rb-readonly-note">Derived from the book above. Recomputed after every save.</p>
      {entries.map((prep) => (
        <article className="rb-appendix-block" key={prep.prep_id}>
          <header className="rb-appendix-head">
            <h3>{prep.name}</h3>
            <span className="rb-headnum rb-headnum-static">
              <strong>{formatQuantity(prep.batch_quantity)}</strong>
              <span className="rb-headnum-label">Batch</span>
            </span>
            <span className="rb-usedby">
              Used by {prep.used_by_item_count} item{prep.used_by_item_count === 1 ? '' : 's'}
            </span>
          </header>
          {prep.used_by.length === 0 ? (
            <p className="rb-empty">Not used by any menu item</p>
          ) : (
            <table className="rb-table rb-table-static">
              <thead>
                <tr>
                  <th className="rb-sno">S.No</th>
                  <th className="rb-name">MENU ITEM</th>
                  <th className="rb-qty">QTY</th>
                  {SHOW_UNIT_COLUMN && <th className="rb-unit">UNIT</th>}
                </tr>
              </thead>
              <tbody>
                {prep.used_by.map((use, i) => (
                  <tr key={`${prep.prep_id}:${use.menu_item_id}`}>
                    <td className="rb-sno">{i + 1}</td>
                    <td className="rb-name">{use.name}</td>
                    <td className="rb-qty">{formatQuantity(use.quantity)}</td>
                    {SHOW_UNIT_COLUMN && <td className="rb-unit">{use.unit_symbol}</td>}
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </article>
      ))}
    </section>
  );
}
